import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({ baseURL: API_BASE, timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r.data,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_info');
      window.location.href = '/login';
    }
    return Promise.reject(new Error(err.response?.data?.message || 'Request failed'));
  }
);

export const adminAPI = {
  // Auth
  login: (email, password) => api.post('/admin/login', { email, password }),
  changePassword: (currentPassword, newPassword) =>
    api.post('/admin/change-password', { currentPassword, newPassword }),

  // Dashboard
  getDashboard:         () => api.get('/admin/dashboard'),
  getDashboardGrowth:   () => api.get('/admin/dashboard/growth'),
  getDashboardActivity: () => api.get('/admin/dashboard/activity'),
  getDashboardExpiring: () => api.get('/admin/dashboard/expiring'),

  // Members
  getMembers:        (params) => api.get('/admin/members', { params }),
  getMember:         (id) => api.get(`/admin/members/${id}`),
  deactivateMember:  (id) => api.post(`/admin/members/${id}/deactivate`),
  deleteMember:      (id) => api.delete(`/admin/members/${id}`),
  updateMemberStatus:(id, status) => api.post(`/admin/members/${id}/status`, { status }),
  exportMembers:     () => api.get('/admin/members/export', { responseType: 'blob' }),

  // Coupons
  getCoupons:   (params) => api.get('/admin/coupons', { params }),

  // Referrals
  getReferrals: () => api.get('/admin/referrals'),

  // Business Central membership reconciliation. dryRun reports what would
  // change without writing — worth running first, since this can downgrade.
  bcSync: (dryRun = false) => api.post('/admin/bc-sync', { dryRun }),

  // Loyalty
  getLoyaltySettings:   () => api.get('/admin/loyalty/settings'),
  saveLoyaltySettings:  (data) => api.put('/admin/loyalty/settings', data),
  getLoyaltyPartners:   () => api.get('/admin/loyalty/partners'),
  createPartner:        (data) => api.post('/admin/loyalty/partners', data),
  updatePartner:        (id, data) => api.put(`/admin/loyalty/partners/${id}`, data),
  deletePartner:        (id) => api.delete(`/admin/loyalty/partners/${id}`),
  getLoyaltyLeaderboard:() => api.get('/admin/loyalty/leaderboard'),

  // App pages
  getPages:  () => api.get('/admin/pages'),
  getPage:   (slug) => api.get(`/admin/pages/${slug}`),
  savePage:  (slug, data) => api.put(`/admin/pages/${slug}`, data),

  // Settings
  getSettings:  () => api.get('/admin/settings'),
  saveSettings: (settings) => api.put('/admin/settings', { settings }),

  // CRM
  getCRMSegments:       () => api.get('/admin/crm/segments'),
  getCRMSegment:        (name, page = 1) => api.get(`/admin/crm/segment/${name}?page=${page}`),
  exportCRMSegment:     (name) => api.get(`/admin/crm/segment/${name}/export`, { responseType: 'blob' }),

  // Push Notifications
  getPushCampaigns: () => api.get('/admin/push/campaigns'),
  sendPushNotification: (data) => api.post('/admin/push/send', data),

  // WhatsApp Campaigns
  getCampaigns:             () => api.get('/admin/campaigns'),
  createCampaign:           (data) => api.post('/admin/campaigns', data),
  getCampaign:              (id) => api.get(`/admin/campaigns/${id}`),
  sendCampaign:             (id) => api.post(`/admin/campaigns/${id}/send`),
  cancelCampaign:           (id) => api.post(`/admin/campaigns/${id}/cancel`),
  deleteCampaign:           (id) => api.delete(`/admin/campaigns/${id}`),
  getCampaignRecipients:    (id, page = 1) => api.get(`/admin/campaigns/${id}/recipients?page=${page}`),
  exportCampaignRecipients: (id) => api.get(`/admin/campaigns/${id}/export`, { responseType: 'blob' }),
  previewAudience:          (data) => api.post('/admin/campaigns/audience/preview', data),
  uploadAudience:           (formData) => api.post('/admin/campaigns/audience/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getWaTemplates:           () => api.get('/admin/campaigns/templates'),
  createWaTemplate:         (data) => api.post('/admin/campaigns/templates', data),
  deleteWaTemplate:         (id) => api.delete(`/admin/campaigns/templates/${id}`),
  syncWaTemplates:          () => api.post('/admin/campaigns/templates/sync'),

  // Promo Codes
  getPromoCodes:           () => api.get('/admin/promo-codes'),
  createPromoCode:         (data) => api.post('/admin/promo-codes', data),
  updatePromoCode:         (id, data) => api.put(`/admin/promo-codes/${id}`, data),
  deletePromoCode:         (id) => api.delete(`/admin/promo-codes/${id}`),
  getPromoRedemptions:     (id, page = 1) => api.get(`/admin/promo-codes/${id}/redemptions?page=${page}`),
  exportPromoRedemptions:  (id) => api.get(`/admin/promo-codes/${id}/export`, { responseType: 'blob' }),

  // Activity audit log
  getActivations:    (params) => api.get('/admin/activations', { params }),
  exportActivations: (params) => api.get('/admin/activations/export', { params, responseType: 'blob' }),

  // Family Members (admin)
  getMemberFamily:         (userId) => api.get(`/admin/members/${userId}/family`),
  addMemberFamily:         (userId, data) => api.post(`/admin/members/${userId}/family`, data),
  revokeMemberFamily:      (userId, familyId) => api.delete(`/admin/members/${userId}/family/${familyId}`),
  reactivateMemberFamily:  (userId, familyId) => api.post(`/admin/members/${userId}/family/${familyId}/reactivate`),
};

export default api;
